import { gql } from '@apollo/client';
import { useMaOpportunitiesContext } from '../../../MaOpportunitiesContext';
import { useQuery } from '@apollo/client';

interface ChartDataPoint {
  group?: string;
  stacks?: Array<{ name?: string }>;
  y?: number;
}

interface UseGetMaOpportunitiesForChartProps {
  skip?: boolean;
  chartData?: ChartDataPoint[];
}

// GraphQL query moved into the hook for self-containment
const MA_OPPORTUNITIES_FOR_CHART_QUERY = gql`
  query maOpportunitiesForChart($filters: MaFilters) {
    maOpportunities(filters: $filters, pagination: { page: 1, perPage: 200 }) {
      totalCount
      opportunities {
        id
        sellerCompany { name }
        sellerStake
        sellerMotivation
        npv
        asset { displayName, name }
        country { displayName }
        group
      }
    }
  }
`;

export const useGetMaOpportunitiesForChart = ({ 
  skip = false, 
  chartData = [], 
}: UseGetMaOpportunitiesForChartProps = {}) => {
  const { filterVariables } = useMaOpportunitiesContext();
  
  // Extract visible groups and stacks from chart data to optimize fetching
  const getVisibleFilters = () => {
    if (!chartData || chartData.length === 0) return { groupFilters: [], stackFilters: [] };
    
    const visibleGroups = new Set<string>();
    const visibleStacks = new Set<string>();
    
    chartData.forEach(d => {
      if (d.group) visibleGroups.add(d.group);
      
      // Handle stacked charts
      if (d.stacks && Array.isArray(d.stacks)) {
        d.stacks.forEach(stack => {
          if (stack && stack.name) visibleStacks.add(stack.name);
        });
      }
      
      // Handle flat charts (no stacking)
      if (!d.stacks && d.y && d.y > 0) {
        visibleGroups.add(d.group!);
      }
    });
    
    return {
      groupFilters: Array.from(visibleGroups),
      stackFilters: Array.from(visibleStacks),
    };
  };
  
  const { groupFilters, stackFilters } = getVisibleFilters();
  
  // Fetch opportunities with optimized filters for chart hover information
  const { data, loading, error } = useQuery(MA_OPPORTUNITIES_FOR_CHART_QUERY, {
    variables: { 
      filters: filterVariables,
      groupFilters,
      stackFilters,
    },
    // Skip if we don't have filter variables, chart data, or explicitly skipped
    skip: skip || !filterVariables || Object.keys(filterVariables).length === 0 || 
          chartData.length === 0,
  });

  return {
    opportunities: data?.maOpportunities?.opportunities || [],
    loading,
    error,
  };
};
